import axios from "axios";

// Use current domain for API calls (works with custom domains)
const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Projects API
export const projectsApi = {
  getAll: () => axios.get(`${API_URL}/projects`, { headers: getAuthHeader() }),
  getOne: (id) => axios.get(`${API_URL}/projects/${id}`, { headers: getAuthHeader() }),
  getDetails: (id) => axios.get(`${API_URL}/projects/${id}/details`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_URL}/projects`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/projects/${id}`, data, { headers: getAuthHeader() }),
  delete: (id, permanent = false) => axios.delete(`${API_URL}/projects/${id}?permanent=${permanent}`, { headers: getAuthHeader() }),
  toggleFavorite: (id) => axios.post(`${API_URL}/projects/${id}/favorite`, {}, { headers: getAuthHeader() }),
  getMyFavorites: () => axios.get(`${API_URL}/projects/favorites/mine`, { headers: getAuthHeader() }),
};

// Activities API
export const activitiesApi = {
  getAll: () => axios.get(`${API_URL}/activities`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_URL}/activities`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/activities/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/activities/${id}`, { headers: getAuthHeader() }),
  syncRates: () => axios.post(`${API_URL}/activities/sync-rates`, {}, { headers: getAuthHeader() }),
};

// Time Entries API
export const timeEntriesApi = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/time-entries${queryString ? `?${queryString}` : ""}`, {
      headers: getAuthHeader(),
    });
  },
  create: (data) => axios.post(`${API_URL}/time-entries`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/time-entries/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/time-entries/${id}`, { headers: getAuthHeader() }),
  copyLast: () => axios.post(`${API_URL}/time-entries/copy-last`, {}, { headers: getAuthHeader() }),
  markInvoiced: (entryIds, invoiced = true) => 
    axios.post(`${API_URL}/time-entries/mark-invoiced`, { entry_ids: entryIds, invoiced }, { headers: getAuthHeader() }),
  toggleInvoiced: (id) =>
    axios.post(`${API_URL}/time-entries/${id}/toggle-invoiced`, {}, { headers: getAuthHeader() }),
};

export const shoppingApi = {
  getAll: () => axios.get(`${API_URL}/shopping`, { headers: getAuthHeader() }),
  getByProject: (projectId) => axios.get(`${API_URL}/shopping/${projectId}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_URL}/shopping`, data, { headers: getAuthHeader() }),
  toggle: (id) => axios.put(`${API_URL}/shopping/${id}/toggle`, {}, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/shopping/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/shopping/${id}`, { headers: getAuthHeader() }),
};

export const documentsApi = {
  getByProject: (projectId) => axios.get(`${API_URL}/documents/${projectId}`, { headers: getAuthHeader() }),
  upload: (projectId, file, description = "") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("project_id", projectId);
    formData.append("description", description);
    return axios.post(`${API_URL}/documents/upload`, formData, {
      headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
    });
  },
  download: (fileId) => `${API_URL}/documents/file/${fileId}`,
  delete: (id) => axios.delete(`${API_URL}/documents/${id}`, { headers: getAuthHeader() }),
};

// Users API
export const usersApi = {
  getAll: () => axios.get(`${API_URL}/users`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_URL}/users`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/users/${id}`, data, { headers: getAuthHeader() }),
  delete: (id, permanent = false) => axios.delete(`${API_URL}/users/${id}?permanent=${permanent}`, { headers: getAuthHeader() }),
  resetPassword: (id, newPassword) => axios.post(`${API_URL}/users/${id}/reset-password`, { new_password: newPassword }, { headers: getAuthHeader() }),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/users/me/avatar`, fd, { headers: getAuthHeader() });
  },
};

export const avatarUrl = (userId, version) => `${API_URL}/users/${userId}/avatar${version ? `?v=${version}` : ""}`;

export const resolveImageUrl = (src) => (src && src.startsWith("/api/") ? `${API_URL}${src.slice(4)}` : src);

// Approvals API
export const approvalsApi = {
  getPending: () => axios.get(`${API_URL}/approvals/pending`, { headers: getAuthHeader() }),
  process: (id, action) =>
    axios.post(`${API_URL}/approvals/${id}`, { action }, { headers: getAuthHeader() }),
  bulkProcess: (ids, action) =>
    axios.post(`${API_URL}/approvals/bulk`, { entry_ids: ids, action }, { headers: getAuthHeader() }),
};

// Reports API
export const reportsApi = {
  getSummary: (filters) =>
    axios.post(`${API_URL}/reports/summary`, filters, { headers: getAuthHeader() }),
  exportCsv: (filters) =>
    axios.post(`${API_URL}/reports/export/csv`, filters, {
      headers: getAuthHeader(),
      responseType: "blob",
    }),
  exportPdf: (filters) =>
    axios.post(`${API_URL}/reports/export/pdf`, filters, {
      headers: getAuthHeader(),
      responseType: "blob",
    }),
  generateInvoice: (projectIds, filters = {}) =>
    axios.post(`${API_URL}/reports/invoice`, { project_ids: projectIds, ...filters }, {
      headers: getAuthHeader(),
    }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API_URL}/dashboard/stats`, { headers: getAuthHeader() }),
  getProfit: (months = 6) => axios.get(`${API_URL}/dashboard/profit?months=${months}`, { headers: getAuthHeader() }),
  getMissingReports: () => axios.get(`${API_URL}/dashboard/missing-reports`, { headers: getAuthHeader() }),
};

// Salary Spec API
export const salaryApi = {
  getMine: (period) => axios.get(`${API_URL}/salary-spec/me${period ? `?period=${period}` : ''}`, { headers: getAuthHeader() }),
  getUser: (userId, period) => axios.get(`${API_URL}/salary-spec/${userId}${period ? `?period=${period}` : ''}`, { headers: getAuthHeader() }),
  getAll: (period) => axios.get(`${API_URL}/salary-spec/all/list${period ? `?period=${period}` : ''}`, { headers: getAuthHeader() }),
  setOverride: (userId, period, overrides) => axios.post(`${API_URL}/salary-spec/${userId}/override?period=${period}`, overrides, { headers: getAuthHeader() }),
  approve: (userId, period) => axios.post(`${API_URL}/salary-spec/${userId}/approve?period=${period}`, {}, { headers: getAuthHeader() }),
  deleteOverride: (userId, period) => axios.delete(`${API_URL}/salary-spec/${userId}/override?period=${period}`, { headers: getAuthHeader() }),
  getPdfUrl: (userId, period) => `${API_URL}/salary-spec/${userId}/pdf?period=${period}`,
  getCustomRange: (userId, start, end) => axios.get(`${API_URL}/salary-spec/${userId}/custom?start=${start}&end=${end}`, { headers: getAuthHeader() }),
  getCompanies: () => axios.get(`${API_URL}/salary-spec/companies/list`, { headers: getAuthHeader() }),
  getCompanyInvoice: (company, params) => axios.get(`${API_URL}/salary-spec/company/${encodeURIComponent(company)}`, { headers: getAuthHeader(), params }),
  getCompanyPdfUrl: (company, params) => `${API_URL}/salary-spec/company/${encodeURIComponent(company)}/pdf?${new URLSearchParams(params).toString()}`,
};

export const emailApi = {
  sendSpec: (data) => axios.post(`${API_URL}/email/send-spec`, data, { headers: getAuthHeader() }),
};

// Inventory API
export const inventoryApi = {
  getAll: (search) => axios.get(`${API_URL}/inventory`, { params: search ? { search } : {}, headers: getAuthHeader() }),
  suggest: (q) => axios.get(`${API_URL}/inventory/suggest`, { params: { q }, headers: getAuthHeader() }),
  add: (data) => axios.post(`${API_URL}/inventory`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/inventory/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/inventory/${id}`, { headers: getAuthHeader() }),
};

// Board/Chat API
export const boardApi = {
  getMessages: (limit) => axios.get(`${API_URL}/board`, { params: limit ? { limit } : {}, headers: getAuthHeader() }),
  post: (text) => axios.post(`${API_URL}/board`, { text }, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/board/${id}`, { headers: getAuthHeader() }),
};

// Clients API
export const clientsApi = {
  getAll: () => axios.get(`${API_URL}/clients`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_URL}/clients`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/clients/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/clients/${id}`, { headers: getAuthHeader() }),
};

// Schedules/Planning API
export const schedulesApi = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/schedules${queryString ? `?${queryString}` : ""}`, {
      headers: getAuthHeader(),
    });
  },
  create: (data) => axios.post(`${API_URL}/schedules`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/schedules/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/schedules/${id}`, { headers: getAuthHeader() }),
  getTypes: () => axios.get(`${API_URL}/schedules/types`, { headers: getAuthHeader() }),
};

export const authApi = {
  changePassword: (currentPassword, newPassword) =>
    axios.post(`${API_URL}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: getAuthHeader() }),
};

// Project Pulse API
export const projectPulseApi = {
  get: (projectId) => axios.get(`${API_URL}/project-pulse/${projectId}`, { headers: getAuthHeader() }),
  getAll: () => axios.get(`${API_URL}/project-pulse/all/summary`, { headers: getAuthHeader() }),
};

// Invoice Import API
export const invoiceImportApi = {
  upload: (file, projectId = null, notes = "") => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);
    if (notes) formData.append("notes", notes);
    return axios.post(`${API_URL}/invoices/upload`, formData, {
      headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
    });
  },
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/invoices${queryString ? `?${queryString}` : ""}`, { headers: getAuthHeader() });
  },
  getOne: (id) => axios.get(`${API_URL}/invoices/${id}`, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_URL}/invoices/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_URL}/invoices/${id}`, { headers: getAuthHeader() }),
  fileUrl: (id) => `${API_URL}/invoices/file/${id}`,
  getComparison: (projectId = null) => {
    const params = projectId ? `?project_id=${projectId}` : "";
    return axios.get(`${API_URL}/invoices/comparison/summary${params}`, { headers: getAuthHeader() });
  },
  fetchFromEmail: () => axios.post(`${API_URL}/invoices/fetch-email`, {}, { headers: getAuthHeader() }),
  getEmailStatus: () => axios.get(`${API_URL}/invoices/email-status`, { headers: getAuthHeader() }),
};

